/**
 * Central routing system
 * All possible routes are managed here, routes are provided
 * with appropriate redirects or an associated view component.
 *
 * Routes that require user data (from authentication) should
 * be wrapped in Auth, and routes that should _only_ appear when
 * a user is not authenticated should be wrapped in skip auth
 * (such they redirect when the user is authenticated). All other
 * routes are accessed regardless.
 *
 * Currently, viewing some routes triggers calls to the APIs to
 * fetch the appropriate data for the view (such as the list of
 * API instances for a user).
 */

import { RouterState } from "connected-react-router"
import React, { useEffect } from "react"
import { connect } from "react-redux"
import { Redirect, Route, RouteComponentProps, Switch } from "react-router"
import { config } from "bridger"
import { initAuth } from "../Actions/Auth/AuthActions"
import { AuthCheck } from "../Components/Common/AuthCheck/AuthCheck"
import { Loader } from "dash-styled"
import { TaskLabelingRoute } from "./Paths/Dashboard/TaskLabelingRoute"
import { DownloadRefHandler } from "../Components/Views/Dashboard/Common/DownloadRefHandler/DownloadRefHandler"
import { AddData } from "../Components/Views/Dashboard/Sections/AppDetail/AddData/AddData"
import { AppCustomizeBaselineDataEdit } from "../Components/Views/Dashboard/Sections/AppDetail/Customize/Modes/BaselineData/BaselineDataEdit"
import {
    BaselineDataSDK,
    BaselineDataSDKType
} from "../Components/Views/Dashboard/Sections/AppDetail/Customize/Modes/BaselineData/Modes/SDK/SDK"
import { BaselineDataSingle } from "../Components/Views/Dashboard/Sections/AppDetail/Customize/Modes/BaselineData/Modes/Single/Single"
import { BaselineDataUpload } from "../Components/Views/Dashboard/Sections/AppDetail/Customize/Modes/BaselineData/Modes/Upload/Upload"
import { JobEdit } from "../Components/Views/Dashboard/Sections/AppDetail/JobsList/JobEdit"
import { Overview } from "../Components/Views/Dashboard/Sections/AppDetail/Overview/Overview"
import { AppList } from "../Components/Views/Dashboard/Sections/AppList/AppList"
import { DataSets } from "../Components/Views/Dashboard/Sections/DataSets/DataSets"
import { Onboarding } from "../Components/Views/Store/Sections/Onboarding/Onboarding"
import { isAuthTransitioning } from "../Helpers/Functions/Selectors/auth"
import {
    APIGroundTruthModel,
    defaultGroundTruthSort
} from "../Models/API/APIGroundTruthModel"
import {
    nameToSlug,
    TemplateName,
    TemplateSlug
} from "../Models/API/AppTemplateModel"
import { APICollaborationModel } from "../Models/API/CollaboratorModel"
import {
    defaultJobSortExpression,
    APIJobQueryModel,
    JobStatus,
    defaultJobMessageSortExpression,
    getJobSortExpression
} from "../Models/API/JobModel"
import {
    encodeFilterAndSort,
    FilterAndSortExpression,
    decodeFilterAndSort
} from "../Models/Client/FilterAndSortExpression"
import { UnaryFilterOperator } from "../Models/Client/FilterExpression"
import { SortExpression } from "../Models/Client/SortExpression"
import { TemplateInstantiateStage } from "../Models/Client/TemplateInstantiation"
import { MainThunkDispatch, State } from "../Reducers"
import { AppGroundTruthDataRoute } from "./Paths/Dashboard/AppGroundTruthDataRoute"
import { ForgotPasswordRoute } from "./Paths/Auth/ForgotPasswordRoute"
import { ResetPasswordRoute } from "./Paths/Auth/ResetPasswordRoute"
import { VerifyOTPRoute } from "./Paths/Auth/VerifyOTPRoute"
import {
    AuthRouteParams,
    encodeAuthParams,
    SigninRoute
} from "./Paths/Auth/SigninRoute"
import { SignupRoute } from "./Paths/Auth/SignupRoute"
import {
    AppCollaboratorsRoute,
    CollaboratorPaths
} from "./Paths/Dashboard/AppCollaboratorsRoute"
import {
    AppDetailRoute,
    AppDetailRouteParams,
    encodeAppDetailRouteParams
} from "./Paths/Dashboard/AppDetailRoute"
import { DashboardHomeRoute } from "./Paths/Dashboard/Home"
import { GeneralSettings } from "../Components/Views/Dashboard/Sections/AppDetail/GeneralSettings"
import { ArticlePath } from "../Models/External/Documentation"
import { InputMode } from "../Components/Common/Render/Variants/JobInput/Common/Container/Container"
import { AppModelListRoute } from "./Paths/Dashboard/AppModelListRoute"
import { AppModelDetailRoute } from "./Paths/Dashboard/AppModelDetailRoute"
import { AppSuperAILabelersRoute } from "./Paths/Dashboard/AppSuperAILabelersRoute"
import { Qualifiers } from "../Components/Views/Dashboard/Sections/AppDetail/Qualifiers/Qualifiers"
import { AppWelcome } from "../Components/Views/Dashboard/Sections/AppDetail/Welcome/Welcome"
import {
    AppInstanceListParams,
    decodeParams as decodeAppInstanceListParams,
    encodeParams as encodeAppInstanceListParams
} from "../Models/API/AppInstanceModel"
import { QualifierTutorialContent } from "../Components/Views/Dashboard/Sections/AppDetail/Qualifiers/Common/QualifierContent/TutorialContent/QualifierTutorialContent"
import { QualifierTestContent } from "../Components/Views/Dashboard/Sections/AppDetail/Qualifiers/Common/QualifierContent/TestContent/QualifierTestContent"
import { SuperAILabelerModel } from "../Models/Client/SuperAILabelerModel"
import {
    AppDetailRouteComponentProps,
    OrgRouteComponentProps
} from "./RouteProps"
import { turbine } from "neql"
import { MemberListRoute } from "./Paths/Organization/MemberListRoute"
import { MemberSignUpRoute } from "./Paths/Organization/MemberSignUpRoute"
import { WorkerListRoute } from "./Paths/Dashboard/WorkerListRoute"
import { WorkerGroupListRoute } from "./Paths/Dashboard/WorkerGroupListRoute"
import { AppWorkerListRoute } from "./Paths/Dashboard/AppWorkerListRoute"
import { AppWorkerGroupListRoute } from "./Paths/Dashboard/AppWorkerGroupListRoute"
import { WorkerGroupAddRoute } from "./Paths/Dashboard/WorkerGroupAddRoute"
import { WorkerAddRoute } from "./Paths/Dashboard/WorkerAddRoute"
import { APIWorker } from "../Models/API/Worker"
import {
    defaultWorkerGroupSort,
    defaultWorkerSort
} from "../Components/Views/Dashboard/Sections/Worker/Common/constants"
import { APIOrgMember } from "../Models/API/APIOrgMember"
import { defaultOrgMemberSort } from "../Components/Views/Dashboard/Sections/Organization/Common/constants"
import { APIWorkerGroup } from "../Models/API/WorkerGroup"
import { APIAppWorker } from "../Models/API/AppWorker"
import { APIAppWorkerGroup } from "../Models/API/AppWorkerGroup"
import {
    defaultAppWorkerGroupSort,
    defaultAppWorkerSort
} from "../Components/Views/Dashboard/Sections/AppDetail/Worker/Common/constants"
import { WorkerSignUpRoute } from "./Paths/Dashboard/WorkerSignUpRoute"
import { AppAuthCheck } from "../Components/Common/AuthCheck/AppAuthCheck"
import { useFreemiumExpiryCountdown } from "../Hooks/useFreemiumExpiryCountdown"
import { AppCollaboratorsSignUpRoute } from "./Paths/Dashboard/AppCollaboratorsSignUpRoute"
import { PlaygroundRoute } from "./Paths/PlaygroundRoute"
import {
    BackofficeCustomerQueryModel,
    decodeBackofficeFilterAndSort,
    decodeBackofficeFilterAndSortToBackofficeParams,
    encodeBackofficeFilterAndSort
} from "../Models/API/BackofficeModel"
import { CustomerList } from "../Components/Views/Dashboard/Sections/Backoffice/CustomerList/CustomerList"
import { BackofficeAuthCheck } from "../Components/Common/AuthCheck/BackofficeAuthCheck"
import { CustomerDetails } from "../Components/Views/Dashboard/Sections/Backoffice/CustomerDetails/CustomerDetails"
import { AppTrainingListRoute } from "./Paths/Dashboard/AppTrainingListRoute"
import { AppTrainingInstanceDetailRoute } from "./Paths/Dashboard/AppTrainingInstanceDetailRoute"
import {
    encodeJobsDetailRouteParams,
    JobDetailRoute,
    JobsDetailRouteParams
} from "./Paths/Dashboard/JobDetailRoute"
import { AppModelsComparisonRoute } from "./Paths/Dashboard/AppModelsComparisonRoute"
import { AppCustomizeJobParametersRoute } from "./Paths/Dashboard/AppCustomizeJobParametersRoute"
import { AppCustomizeLLMParametersRoute } from "./Paths/Dashboard/AppCustomizeLLMParametersRoute"
import { AppCustomizeTaskParametersRoute } from "./Paths/Dashboard/AppCustomizeTaskParametersRoute"
import { OrgChooser } from "../Components/Views/Store/Sections/Onboarding/Stages/OrgChooser"

interface StateProps {
    isWorking: boolean
    router: RouterState
    onboardingTemplate?: TemplateName
    shouldResetScrollY: boolean
}

interface DispatchProps {
    initialise: () => void
}

export type Props = StateProps & DispatchProps

interface RouteInfo {
    component: any
    exact?: boolean
}

function withSort<T>(
    path: string,
    sort: SortExpression<T> | undefined,
    defaultSort: SortExpression<T>
): string {
    const params =
        sort === undefined
            ? undefined
            : encodeFilterAndSort({ filter: [], sort }, defaultSort)

    return `${path}${params === undefined ? "" : `?${params.toString()}`}`
}

export const routePaths = {
    home: () => config.dash.home,
    // Dashboard routes  (req. auth)
    dashboard: () => "/dashboard",

    orgHome: (orgUsername: string) => `/u/${orgUsername}`,

    appList: (orgUsername: string, params?: AppInstanceListParams) => {
        const search =
            params === undefined
                ? undefined
                : encodeAppInstanceListParams(params)

        return `/u/${orgUsername}/projects${
            search === undefined ? "" : `?${search.toString()}`
        }`
    },

    appWelcome: (orgUsername: string, appID: string) =>
        `/u/${orgUsername}/projects/${appID}`,
    appDetail: (
        orgUsername: string,
        appID: string,
        params?: AppDetailRouteParams,
        selected?: number[],
        jobID?: string
    ) => {
        const s =
            params === undefined
                ? undefined
                : encodeAppDetailRouteParams(params)

        const search = s === undefined ? "" : `?${s.toString()}`

        const sel =
            selected && selected.length > 0
                ? `${search === "" ? "?" : "&"}selected=[${selected.join(",")}]`
                : ""

        const selectedJob = jobID
            ? `${search === "" && sel === "" ? "?" : "&"}job=${jobID}`
            : ""

        return `/u/${orgUsername}/projects/${appID}/jobs${search}${sel}${selectedJob}`
    },
    appDetailLabeling: (
        orgUsername: string,
        appID: string,
        filterAndSort?: AppDetailRouteParams,
        selected?: number[]
    ) => {
        const searchFS =
            filterAndSort === undefined
                ? undefined
                : encodeAppDetailRouteParams(filterAndSort)

        const search = searchFS === undefined ? "" : `?${searchFS.toString()}`

        const sel =
            selected && selected.length > 0
                ? `${search === "" ? "?" : "&"}selected=[${selected.join(",")}]`
                : ""

        return `/u/${orgUsername}/projects/${appID}/tasks${search}${sel}`
    },
    jobDetailLabeling: (
        orgUsername: string,
        appID: string,
        jobID: string,
        filterAndSort?: JobsDetailRouteParams,
        selected?: number[]
    ) => {
        const searchFS =
            filterAndSort === undefined
                ? undefined
                : encodeJobsDetailRouteParams(filterAndSort)

        const search = searchFS === undefined ? "" : `?${searchFS.toString()}`

        const sel =
            selected && selected.length > 0
                ? `${search === "" ? "?" : "&"}selected=[${selected.join(",")}]`
                : ""

        return `/u/${orgUsername}/projects/${appID}/jobs/${jobID}/tasks${search}${sel}`
    },
    appDetailOverview: (orgUsername: string, appID: string) =>
        `/u/${orgUsername}/projects/${appID}/statistics`,
    appDetailReviewJobs: (
        orgUsername: string,
        appID: string,
        sort?: SortExpression<APIJobQueryModel>
    ) =>
        routePaths.appDetail(orgUsername, appID, {
            clearFilterOnCloseModal: true,
            openDetail: true,
            sort: getJobSortExpression(sort),
            filter: [
                {
                    fieldName: "status",
                    operator: UnaryFilterOperator.eq,
                    value: JobStatus.COMPLETED
                },
                {
                    fieldName: "correct",
                    operator: UnaryFilterOperator.eq,
                    value: null
                }
            ]
        }),
    appDetailMessages: (
        orgUsername: string,
        appID: string,
        params?: AppDetailRouteParams,
        selected?: number[]
    ) => {
        const s =
            params === undefined
                ? undefined
                : encodeAppDetailRouteParams(
                      params,
                      defaultJobMessageSortExpression
                  )

        const search = s === undefined ? "" : `?${s.toString()}`

        const sel =
            selected && selected.length > 0
                ? `${search === "" ? "?" : "&"}selected=[${selected.join(",")}]`
                : ""
        return `/u/${orgUsername}/projects/${appID}/messages${search}${sel}`
    },
    jobsDetail: (
        orgUsername: string,
        appID: string,
        jobID: string,
        params?: JobsDetailRouteParams,
        selected?: number[]
    ) => {
        const s =
            params === undefined
                ? undefined
                : encodeJobsDetailRouteParams(params)

        const search = s === undefined ? "" : `?${s.toString()}`

        const sel =
            selected && selected.length > 0
                ? `${search === "" ? "?" : "&"}selected=[${selected.join(",")}]`
                : ""

        return `/u/${orgUsername}/projects/${appID}/jobs/${jobID}${search}${sel}`
    },
    jobsReview: (
        orgUsername: string,
        appID: string,
        jobID: string,
        params?: JobsDetailRouteParams
    ) => {
        const s =
            params === undefined
                ? undefined
                : encodeJobsDetailRouteParams(params)

        const search = s === undefined ? "" : `?${s.toString()}`

        return `/u/${orgUsername}/projects/${appID}/jobs/${jobID}/review${search}`
    },
    jobsMessageDetail: (
        orgUsername: string,
        appID: string,
        jobID: string,
        params?: JobsDetailRouteParams,
        selected?: number[]
    ) => {
        const s =
            params === undefined
                ? undefined
                : encodeJobsDetailRouteParams(
                      params,
                      defaultJobMessageSortExpression
                  )

        const search = s === undefined ? "" : `?${s.toString()}`

        const sel =
            selected && selected.length > 0
                ? `${search === "" ? "?" : "&"}selected=[${selected.join(",")}]`
                : ""

        return `/u/${orgUsername}/projects/${appID}/messages/${jobID}${search}${sel}`
    },
    jobsMessageReview: (
        orgUsername: string,
        appID: string,
        jobID: string,
        params?: JobsDetailRouteParams
    ) => {
        const s =
            params === undefined
                ? undefined
                : encodeJobsDetailRouteParams(params)

        const search = s === undefined ? "" : `?${s.toString()}`

        return `/u/${orgUsername}/projects/${appID}/messages/${jobID}/review${search}`
    },
    jobEdit: (
        orgUsername: string,
        appID: string,
        jobID: string,
        params?: JobsDetailRouteParams
    ) => {
        const search =
            params === undefined
                ? undefined
                : encodeJobsDetailRouteParams(params, defaultJobSortExpression)

        return `/u/${orgUsername}/projects/${appID}/jobs/${jobID}/edit${
            search === undefined ? "" : `?${search.toString()}`
        }`
    },
    taskDetails: (
        orgUsername: string,
        appID: string,
        jobID: string,
        version: string,
        taskID: string,
        params?: JobsDetailRouteParams,
        selected?: number[]
    ) => {
        const s =
            params === undefined
                ? undefined
                : encodeJobsDetailRouteParams(params)

        const search = s === undefined ? "" : `?${s.toString()}`

        const sel =
            selected && selected.length > 0
                ? `${search === "" ? "?" : "&"}selected=[${selected.join(",")}]`
                : ""

        return `/u/${orgUsername}/projects/${appID}/jobs/${jobID}/version/${version}/task/${taskID}${search}${sel}`
    },
    taskDetailsMessages: (
        orgUsername: string,
        appID: string,
        jobID: string,
        version: string,
        taskID: string,
        params?: JobsDetailRouteParams,
        selected?: number[]
    ) => {
        const s =
            params === undefined
                ? undefined
                : encodeJobsDetailRouteParams(
                      params,
                      defaultJobMessageSortExpression
                  )

        const search = s === undefined ? "" : `?${s.toString()}`

        const sel =
            selected && selected.length > 0
                ? `${search === "" ? "?" : "&"}selected=[${selected.join(",")}]`
                : ""

        return `/u/${orgUsername}/projects/${appID}/messages/${jobID}/version/${version}/task/${taskID}${search}${sel}`
    },
    appCustomize: (orgUsername: string, appID: string) =>
        `/u/${orgUsername}/projects/${appID}/customize`,
    // TODO: check availability
    appCustomizeMetrics: (orgUsername: string, appID: string) =>
        `/u/${orgUsername}/projects/${appID}/metrics`,

    appSettings: (orgUsername: string, appID: string) =>
        `/u/${orgUsername}/projects/${appID}/general-settings`,

    appCustomizeJobParameters: (orgUsername: string, appID: string) =>
        `/u/${orgUsername}/projects/${appID}/parameters/job`,

    appCustomizeLLMParameters: (orgUsername: string, appID: string) =>
        `/u/${orgUsername}/projects/${appID}/parameters/llm`,

    appCustomizeTaskParameters: (orgUsername: string, appID: string) =>
        `/u/${orgUsername}/projects/${appID}/parameters/task`,

    appCustomizeBaselineData: (
        orgUsername: string,
        appID: string,
        filterAndSort?: FilterAndSortExpression<APIGroundTruthModel>,
        openDetail?: boolean
    ) => {
        let search = encodeFilterAndSort(filterAndSort, defaultGroundTruthSort)

        if (openDetail) {
            if (search === undefined) {
                search = new URLSearchParams()
            }
            search.set("open", "true")
        }

        return `/u/${orgUsername}/projects/${appID}/ground-truth${
            search === undefined ? "" : `?${search.toString()}`
        }`
    },
    appCustomizeBaselineDataAddData: (orgUsername: string, appID: string) =>
        `/u/${orgUsername}/projects/${appID}/ground-truth/add-data`,
    appCustomizeBaselineDataAddDataCurl: (orgUsername: string, appID: string) =>
        `/u/${orgUsername}/projects/${appID}/ground-truth/add-data/curl`,
    appCustomizeBaselineDataAddDataPython: (
        orgUsername: string,
        appID: string
    ) => `/u/${orgUsername}/projects/${appID}/ground-truth/add-data/python`,
    appCustomizeBaselineDataAddDataCLI: (orgUsername: string, appID: string) =>
        `/u/${orgUsername}/projects/${appID}/ground-truth/add-data/cli`,
    // TODO: fix page render or remove it
    appCustomizeBaselineDataAddDataSingle: (
        orgUsername: string,
        appID: string
    ) => `/u/${orgUsername}/projects/${appID}/ground-truth/add-data/single`,
    appCustomizeBaselineDataAddDataUpload: (
        orgUsername: string,
        appID: string
    ) => `/u/${orgUsername}/projects/${appID}/ground-truth/add-data/upload`,
    appCustomizeBaselineDataDetail: (
        orgUsername: string,
        appID: string,
        baselineID: string,
        filterAndSort?: FilterAndSortExpression<APIGroundTruthModel>
    ) => {
        const search = encodeFilterAndSort(
            filterAndSort,
            defaultGroundTruthSort
        )

        return `/u/${orgUsername}/projects/${appID}/ground-truth/${baselineID}${
            search === undefined ? "" : `?${search.toString()}`
        }`
    },
    appCustomizeBaselineDataEdit: (
        orgUsername: string,
        appID: string,
        baselineID: string,
        filterAndSort?: FilterAndSortExpression<APIGroundTruthModel>
    ) => {
        const search = encodeFilterAndSort(
            filterAndSort,
            defaultGroundTruthSort
        )

        return `/u/${orgUsername}/projects/${appID}/ground-truth/${baselineID}/edit${
            search === undefined ? "" : `?${search.toString()}`
        }`
    },
    appAddData: (orgUsername: string, appID: string, mode: string) =>
        `/u/${orgUsername}/projects/${appID}/jobs/add-data/${mode}`,
    appCollaborators: (
        orgUsername: string,
        appID: string,
        filterAndSort?: FilterAndSortExpression<APICollaborationModel>,
        path?: CollaboratorPaths
    ) => {
        const sPath = new URLSearchParams()
        if (path) {
            sPath.set("path", path)
        }
        const search = encodeFilterAndSort(filterAndSort)
        if (search) {
            search.forEach((v, k) => {
                sPath.set(k, v)
            })
        }

        return `/u/${orgUsername}/projects/${appID}/collaborators${
            path === undefined && search === undefined
                ? ""
                : `?${sPath.toString()}`
        }`
    },
    appCollaboratorsSignUp: (
        orgUsername: string,
        appID: string,
        collaborationID: string,
        code?: string
    ) => {
        const search = new URLSearchParams()

        if (code !== undefined) {
            search.set("verification_code", code)
        }

        return `/u/${orgUsername}/projects/${appID}/collaborators/${collaborationID}/signup${
            Array.from(search.keys()).length > 0 ? `?${search.toString()}` : ""
        }`
    },
    // TODO: check availability
    appSuperAILabelers: (
        orgUsername: string,
        appID: string,
        filterAndSort?: FilterAndSortExpression<SuperAILabelerModel>
    ) => {
        const search = encodeFilterAndSort(filterAndSort)

        return `/u/${orgUsername}/projects/${appID}/superai-labelers${
            search === undefined ? "" : `?${search.toString()}`
        }`
    },
    appQualifiers: (orgUsername: string, appID: string) =>
        `/u/${orgUsername}/projects/${appID}/qualifiers`,
    appQualifierTestContent: (
        orgUsername: string,
        appID: string,
        qualifierID: string,
        filterAndSort?: FilterAndSortExpression<APIGroundTruthModel>
    ) => {
        let search = encodeFilterAndSort(filterAndSort, defaultGroundTruthSort)

        return `/u/${orgUsername}/projects/${appID}/qualifiers/${
            turbine.TrainingType.Test
        }/${qualifierID}/content${
            search === undefined ? "" : `?${search.toString()}`
        }`
    },
    appQualifierTutorialContent: (
        orgUsername: string,
        appID: string,
        qualifierID: string,
        filterAndSort?: FilterAndSortExpression<APIJobQueryModel>
    ) => {
        let search = encodeFilterAndSort(
            filterAndSort,
            defaultJobSortExpression
        )

        return `/u/${orgUsername}/projects/${appID}/qualifiers/${
            turbine.TrainingType.Tutorial
        }/${qualifierID}/content${
            search === undefined ? "" : `?${search.toString()}`
        }`
    },
    appQualifierTestContentBaselineDataDetail: (
        orgUsername: string,
        appID: string,
        qualifierID: string,
        baselineID: string,
        filterAndSort?: FilterAndSortExpression<APIGroundTruthModel>
    ) => {
        const search = encodeFilterAndSort(
            filterAndSort,
            defaultGroundTruthSort
        )

        return `/u/${orgUsername}/projects/${appID}/qualifiers/${
            turbine.TrainingType.Test
        }/${qualifierID}/content/${baselineID}/detail${
            search === undefined ? "" : `?${search.toString()}`
        }`
    },
    appModelList: (orgUsername: string, appID: string) =>
        `/u/${orgUsername}/projects/${appID}/models`,
    appModelDetail: (orgUsername: string, appID: string, modelID: string) =>
        `/u/${orgUsername}/projects/${appID}/models/${modelID}`,
    appWorkerList: (
        orgUsername: string,
        appID: string,
        sort?: SortExpression<APIAppWorker>
    ) =>
        withSort(
            `/u/${orgUsername}/projects/${appID}/workers`,
            sort,
            defaultAppWorkerSort
        ),
    appWorkerGroupList: (
        orgUsername: string,
        appID: string,
        sort?: SortExpression<APIAppWorkerGroup>
    ) =>
        withSort(
            `/u/${orgUsername}/projects/${appID}/worker-groups`,
            sort,
            defaultAppWorkerGroupSort
        ),
    appTrainingList: (orgUsername: string, appID: string) =>
        `/u/${orgUsername}/projects/${appID}/trainings`,
    appModelsComparison: (
        orgUsername: string,
        appID: string,
        datasetID?: string
    ) => {
        const params = new URLSearchParams()
        if (datasetID !== undefined) {
            params.set("dataset_id", datasetID)
        }
        return `/u/${orgUsername}/projects/${appID}/models-comparison${
            Array.from(params.keys()).length > 0 ? `?${params.toString()}` : ""
        }`
    },
    appTrainingInstanceDetail: (
        orgUsername: string,
        appID: string,
        instanceID: string
    ) => `/u/${orgUsername}/projects/${appID}/trainings/${instanceID}`,
    datasets: (orgUsername: string) => `/u/${orgUsername}/datasets`,

    orgMemberList: (orgUsername: string, sort?: SortExpression<APIOrgMember>) =>
        withSort(`/u/${orgUsername}/members`, sort, defaultOrgMemberSort),
    orgMemberSignup: (orgUsername: string, memberId: string, code?: string) => {
        const params = new URLSearchParams()

        if (code !== undefined) {
            params.set("code", code)
        }

        return `/u/${orgUsername}/members/${memberId}/signup${
            Array.from(params.keys()).length > 0 ? `?${params.toString()}` : ""
        }`
    },
    orgWorkerList: (orgUsername: string, sort?: SortExpression<APIWorker>) =>
        withSort(`/u/${orgUsername}/workers`, sort, defaultWorkerSort),
    orgWorkerAdd: (orgUsername: string) => `/u/${orgUsername}/workers/new`,
    orgWorkerSignup: (orgUsername: string, memberId: string, code?: string) => {
        const params = new URLSearchParams()

        if (code !== undefined) {
            params.set("code", code)
        }

        return `/u/${orgUsername}/workers/${memberId}/signup${
            Array.from(params.keys()).length > 0 ? `?${params.toString()}` : ""
        }`
    },
    orgWorkerGroupList: (
        orgUsername: string,
        sort?: SortExpression<APIWorkerGroup>
    ) =>
        withSort(
            `/u/${orgUsername}/worker-groups`,
            sort,
            defaultWorkerGroupSort
        ),
    orgWorkerGroupAdd: (orgUsername: string) =>
        `/u/${orgUsername}/worker-groups/new`,

    // Store routes
    store: () => config.dash.store,
    // onboarding
    onboardingChooseOrganization: (templateSlug: TemplateSlug) => {
        return `/store/templates/${templateSlug}`
    },
    onboardingCreateJob: (templateSlug: TemplateSlug, mode: string) =>
        `/store/templates/${templateSlug}/${TemplateInstantiateStage.createJob}/${mode}`,
    onboardingSetParameters: (templateSlug: TemplateSlug) =>
        `/store/templates/${templateSlug}/${TemplateInstantiateStage.setParameters}`,
    onboardingSetUser: (templateSlug: TemplateSlug) =>
        `/store/templates/${templateSlug}/${TemplateInstantiateStage.setUser}`,
    onboardingLoginUser: (templateSlug: TemplateSlug) =>
        `/store/templates/${templateSlug}/${TemplateInstantiateStage.loginUser}`,
    onboardingSetLabeler: (templateSlug: TemplateSlug) =>
        `/store/templates/${templateSlug}/${TemplateInstantiateStage.selectLabeler}`,
    onboardingPrepareLabeling: (templateSlug: TemplateSlug) =>
        `/store/templates/${templateSlug}/${TemplateInstantiateStage.prepareLabeling}`,
    onboardingSelectProject: (templateSlug: TemplateSlug) =>
        `/store/templates/${templateSlug}/${TemplateInstantiateStage.selectProject}`,
    // Authentication
    authSignUp: () => "/go_go_go",
    authSignIn: (params: AuthRouteParams = {}) => {
        const query = encodeAuthParams(params)

        return `/auth/signin${
            query === undefined ? "" : `?${query.toString()}`
        }`
    },
    authForgotPassword: () => "/auth/forgot-password",
    authResetPassword: () => "/auth/reset-password",
    authVerify: () => "/auth/verify",

    // Backfoffice
    customers: (
        params?: FilterAndSortExpression<BackofficeCustomerQueryModel>
    ) => {
        const search = encodeBackofficeFilterAndSort(params)

        return `/backoffice/customers${
            search === undefined ? "" : `?${search.toString()}`
        }`
    },
    customerDetails: (username: string) => {
        return `/backoffice/customers/${username}`
    },
    // Docs routes
    // TODO: check availability
    docs: (articlePath?: ArticlePath) =>
        `/docs${articlePath === undefined ? "" : `/${articlePath}`}`,
    // TODO: check availability
    docsApiReference: () => "/reference",

    // pages for cookie information
    // TODO: check availability
    cookies: () => `/cookies`,

    // test path for testing components
    test: () => "/test",

    schemaPlayground: () => "/playground"
} as const

export const abstractRoutePaths: {
    [Property in keyof typeof routePaths]: string
} = {
    home: routePaths.home(),
    dashboard: routePaths.dashboard(),
    orgHome: routePaths.orgHome(":orgUsername"),
    appList: routePaths.appList(":orgUsername"),
    appWelcome: routePaths.appWelcome(":orgUsername", ":appID"),
    appDetail: routePaths.appDetail(":orgUsername", ":appID"),
    appDetailLabeling: routePaths.appDetailLabeling(":orgUsername", ":appID"),
    jobDetailLabeling: routePaths.jobDetailLabeling(
        ":orgUsername",
        ":appID",
        ":jobID"
    ),
    appDetailOverview: routePaths.appDetailOverview(":orgUsername", ":appID"),
    appDetailReviewJobs: routePaths.appDetailReviewJobs(
        ":orgUsername",
        ":appID"
    ),
    appDetailMessages: routePaths.appDetailMessages(":orgUsername", ":appID"),
    jobsDetail: routePaths.jobsDetail(":orgUsername", ":appID", ":jobID"),
    jobsReview: routePaths.jobsReview(":orgUsername", ":appID", ":jobID"),
    jobsMessageDetail: routePaths.jobsMessageDetail(
        ":orgUsername",
        ":appID",
        ":jobID"
    ),
    jobsMessageReview: routePaths.jobsMessageReview(
        ":orgUsername",
        ":appID",
        ":jobID"
    ),
    jobEdit: routePaths.jobEdit(":orgUsername", ":appID", ":jobID"),
    taskDetails: routePaths.taskDetails(
        ":orgUsername",
        ":appID",
        ":jobID",
        ":version",
        ":taskID"
    ),
    taskDetailsMessages: routePaths.taskDetailsMessages(
        ":orgUsername",
        ":appID",
        ":jobID",
        ":version",
        ":taskID"
    ),
    appCustomize: routePaths.appCustomize(":orgUsername", ":appID"),
    appCustomizeMetrics: routePaths.appCustomizeMetrics(
        ":orgUsername",
        ":appID"
    ),
    appSettings: routePaths.appSettings(":orgUsername", ":appID"),
    appCustomizeJobParameters: routePaths.appCustomizeJobParameters(
        ":orgUsername",
        ":appID"
    ),
    appCustomizeLLMParameters: routePaths.appCustomizeLLMParameters(
        ":orgUsername",
        ":appID"
    ),
    appCustomizeTaskParameters: routePaths.appCustomizeTaskParameters(
        ":orgUsername",
        ":appID"
    ),
    appCustomizeBaselineData: routePaths.appCustomizeBaselineData(
        ":orgUsername",
        ":appID"
    ),
    appCustomizeBaselineDataAddData: routePaths.appCustomizeBaselineDataAddData(
        ":orgUsername",
        ":appID"
    ),
    appCustomizeBaselineDataAddDataCurl:
        routePaths.appCustomizeBaselineDataAddDataCurl(
            ":orgUsername",
            ":appID"
        ),
    appCustomizeBaselineDataAddDataPython:
        routePaths.appCustomizeBaselineDataAddDataPython(
            ":orgUsername",
            ":appID"
        ),
    appCustomizeBaselineDataAddDataCLI:
        routePaths.appCustomizeBaselineDataAddDataCLI(":orgUsername", ":appID"),
    appCustomizeBaselineDataAddDataSingle:
        routePaths.appCustomizeBaselineDataAddDataSingle(
            ":orgUsername",
            ":appID"
        ),
    appCustomizeBaselineDataAddDataUpload:
        routePaths.appCustomizeBaselineDataAddDataUpload(
            ":orgUsername",
            ":appID"
        ),
    appCustomizeBaselineDataDetail: routePaths.appCustomizeBaselineDataDetail(
        ":orgUsername",
        ":appID",
        ":baselineID"
    ),
    appCustomizeBaselineDataEdit: routePaths.appCustomizeBaselineDataEdit(
        ":orgUsername",
        ":appID",
        ":baselineID"
    ),
    appAddData: routePaths.appAddData(":orgUsername", ":appID", ":mode"),
    appCollaborators: routePaths.appCollaborators(":orgUsername", ":appID"),
    appCollaboratorsSignUp: routePaths.appCollaboratorsSignUp(
        ":orgUsername",
        ":appID",
        ":collaborationID"
    ),
    appSuperAILabelers: routePaths.appSuperAILabelers(":orgUsername", ":appID"),
    appQualifiers: routePaths.appQualifiers(":orgUsername", ":appID"),
    appQualifierTestContent: routePaths.appQualifierTestContent(
        ":orgUsername",
        ":appID",
        ":qualifierID"
    ),
    appQualifierTutorialContent: routePaths.appQualifierTutorialContent(
        ":orgUsername",
        ":appID",
        ":qualifierID"
    ),
    appQualifierTestContentBaselineDataDetail:
        routePaths.appQualifierTestContentBaselineDataDetail(
            ":orgUsername",
            ":appID",
            ":qualifierID",
            ":baselineID"
        ),
    appModelList: routePaths.appModelList(":orgUsername", ":appID"),
    appModelDetail: routePaths.appModelDetail(
        ":orgUsername",
        ":appID",
        ":modelID"
    ),
    appWorkerList: routePaths.appWorkerList(":orgUsername", ":appID"),
    appWorkerGroupList: routePaths.appWorkerGroupList(":orgUsername", ":appID"),
    appTrainingList: routePaths.appTrainingList(":orgUsername", ":appID"),
    appModelsComparison: routePaths.appModelsComparison(
        ":orgUsername",
        ":appID"
    ),
    appTrainingInstanceDetail: routePaths.appTrainingInstanceDetail(
        ":orgUsername",
        ":appID",
        ":instanceID"
    ),
    datasets: routePaths.datasets(":orgUsername"),
    orgMemberList: routePaths.orgMemberList(":orgUsername"),
    orgMemberSignup: routePaths.orgMemberSignup(":orgUsername", ":memberId"),
    orgWorkerList: routePaths.orgWorkerList(":orgUsername"),
    orgWorkerSignup: routePaths.orgWorkerSignup(":orgUsername", ":workerId"),
    orgWorkerAdd: routePaths.orgWorkerAdd(":orgUsername"),
    orgWorkerGroupList: routePaths.orgWorkerGroupList(":orgUsername"),
    orgWorkerGroupAdd: routePaths.orgWorkerGroupAdd(":orgUsername"),
    store: routePaths.store(),
    onboardingChooseOrganization: routePaths.onboardingChooseOrganization(
        TemplateSlug(":templateSlug")
    ),
    onboardingCreateJob: routePaths.onboardingCreateJob(
        TemplateSlug(":templateSlug"),
        ":mode"
    ),
    onboardingSetParameters: routePaths.onboardingSetParameters(
        TemplateSlug(":templateSlug")
    ),
    onboardingSetUser: routePaths.onboardingSetUser(
        TemplateSlug(":templateSlug")
    ),
    onboardingLoginUser: routePaths.onboardingLoginUser(
        TemplateSlug(":templateSlug")
    ),
    onboardingSetLabeler: routePaths.onboardingSetLabeler(
        TemplateSlug(":templateSlug")
    ),
    onboardingPrepareLabeling: routePaths.onboardingPrepareLabeling(
        TemplateSlug(":templateSlug")
    ),
    onboardingSelectProject: routePaths.onboardingSelectProject(
        TemplateSlug(":templateSlug")
    ),
    authSignUp: routePaths.authSignUp(),
    authSignIn: routePaths.authSignIn(),
    authForgotPassword: routePaths.authForgotPassword(),
    authResetPassword: routePaths.authResetPassword(),
    authVerify: routePaths.authVerify(),
    customers: routePaths.customers(),
    customerDetails: routePaths.customerDetails(":username"),
    docs: routePaths.docs(),
    docsApiReference: routePaths.docsApiReference(),
    cookies: routePaths.cookies(),
    test: routePaths.test(),
    schemaPlayground: routePaths.schemaPlayground()
}

export const isOpenRoute = (route: string) =>
    !route.includes(routePaths.dashboard()) &&
    !route.startsWith("/u/") &&
    !route.startsWith("/backoffice") &&
    !route.startsWith("/store/templates/")

export const routeMap = (() => {
    const dashboardRoutes: [string, RouteInfo][] = [
        /**
         * Dashboard routes
         */
        [
            abstractRoutePaths.orgHome,
            {
                component: (props: OrgRouteComponentProps) => {
                    return (
                        <Redirect
                            to={routePaths.appList(
                                props.match.params.orgUsername
                            )}
                        />
                    )
                },
                exact: true
            }
        ],
        [
            abstractRoutePaths.appList,
            {
                component: (props: OrgRouteComponentProps) => {
                    const search = new URLSearchParams(props.location.search)
                    const params = decodeAppInstanceListParams(search)

                    return (
                        <AuthCheck>
                            <DownloadRefHandler
                                downloadRef={search.get("d") || undefined}
                                appID="root"
                                redirectToOnClose={routePaths.appList(
                                    props.match.params.orgUsername,
                                    params
                                )}
                            >
                                <AppList
                                    orgUsername={props.match.params.orgUsername}
                                    params={params}
                                />
                            </DownloadRefHandler>
                        </AuthCheck>
                    )
                },
                exact: true
            }
        ],
        [
            abstractRoutePaths.appDetailOverview,
            {
                component: ({ match }: AppDetailRouteComponentProps) => {
                    return (
                        <AppAuthCheck {...match.params} scope="stats:view">
                            <Overview {...match.params} />
                        </AppAuthCheck>
                    )
                },
                exact: true
            }
        ],
        [
            abstractRoutePaths.appCustomize,
            {
                component: (props: AppDetailRouteComponentProps) => {
                    const { appID, orgUsername } = props.match.params
                    return (
                        <AuthCheck>
                            <Redirect
                                to={routePaths.appCustomizeJobParameters(
                                    orgUsername,
                                    appID
                                )}
                            />
                        </AuthCheck>
                    )
                },
                exact: true
            }
        ],
        [
            abstractRoutePaths.appCustomizeMetrics,
            {
                component: (props: AppDetailRouteComponentProps) => {
                    const { appID, orgUsername } = props.match.params

                    return (
                        <Redirect
                            to={routePaths.appDetail(orgUsername, appID)}
                        />
                    )
                }
            }
        ],
        [
            abstractRoutePaths.appSettings,
            {
                component: ({ match }: AppDetailRouteComponentProps) => {
                    return (
                        <AppAuthCheck {...match.params} scope="settings:view">
                            <GeneralSettings {...match.params} />
                        </AppAuthCheck>
                    )
                }
            }
        ],
        [
            abstractRoutePaths.appWelcome,
            {
                component: ({
                    match,
                    location
                }: AppDetailRouteComponentProps) => {
                    const state = location.state as any
                    return (
                        <AppAuthCheck {...match.params} scope="app:view">
                            <AppWelcome
                                {...match.params}
                                onboardingCompleted={state?.onboardingCompleted}
                            />
                        </AppAuthCheck>
                    )
                },
                exact: true
            }
        ],
        [
            abstractRoutePaths.appCustomizeBaselineDataAddDataCurl,
            {
                component: ({ match }: AppDetailRouteComponentProps) => {
                    return (
                        <AppAuthCheck
                            {...match.params}
                            scope="ground-truth:create"
                        >
                            <BaselineDataSDK
                                {...match.params}
                                type={BaselineDataSDKType.curl}
                            />
                        </AppAuthCheck>
                    )
                }
            }
        ],
        [
            abstractRoutePaths.appCustomizeBaselineDataAddDataPython,
            {
                component: ({ match }: AppDetailRouteComponentProps) => {
                    return (
                        <AppAuthCheck
                            {...match.params}
                            scope="ground-truth:create"
                        >
                            <BaselineDataSDK
                                {...match.params}
                                type={BaselineDataSDKType.python}
                            />
                        </AppAuthCheck>
                    )
                }
            }
        ],
        [
            abstractRoutePaths.appCustomizeBaselineDataAddDataCLI,
            {
                component: ({ match }: AppDetailRouteComponentProps) => {
                    return (
                        <AppAuthCheck
                            {...match.params}
                            scope="ground-truth:create"
                        >
                            <BaselineDataSDK
                                {...match.params}
                                type={BaselineDataSDKType.cli}
                            />
                        </AppAuthCheck>
                    )
                }
            }
        ],
        [
            abstractRoutePaths.appCustomizeBaselineDataAddDataSingle,
            {
                component: ({ match }: AppDetailRouteComponentProps) => {
                    return (
                        <AppAuthCheck
                            {...match.params}
                            scope="ground-truth:create"
                        >
                            <BaselineDataSingle {...match.params} />
                        </AppAuthCheck>
                    )
                }
            }
        ],
        [
            abstractRoutePaths.appCustomizeBaselineDataAddDataUpload,
            {
                component: ({ match }: AppDetailRouteComponentProps) => {
                    return (
                        <AppAuthCheck
                            {...match.params}
                            scope="ground-truth:create"
                        >
                            <BaselineDataUpload {...match.params} />
                        </AppAuthCheck>
                    )
                }
            }
        ],
        [
            abstractRoutePaths.appCustomizeBaselineDataAddData,
            {
                component: (props: AppDetailRouteComponentProps) => {
                    const { appID, orgUsername } = props.match.params

                    return (
                        <AuthCheck>
                            <Redirect
                                to={routePaths.appCustomizeBaselineDataAddDataCurl(
                                    orgUsername,
                                    appID
                                )}
                            />
                        </AuthCheck>
                    )
                }
            }
        ],
        [
            abstractRoutePaths.appCustomizeBaselineDataEdit,
            {
                component: ({
                    match,
                    location
                }: AppDetailRouteComponentProps<{
                    baselineID: string
                }>) => {
                    const { appID, baselineID, orgUsername } = match.params
                    const filterAndSort = decodeFilterAndSort(
                        new URLSearchParams(location.search),
                        defaultGroundTruthSort
                    )

                    if (filterAndSort.isError) {
                        return (
                            <Redirect
                                to={routePaths.appCustomizeBaselineDataEdit(
                                    orgUsername,
                                    appID,
                                    baselineID
                                )}
                            />
                        )
                    }

                    return (
                        <AppAuthCheck
                            {...match.params}
                            scope="ground-truth:edit"
                        >
                            <AppCustomizeBaselineDataEdit
                                orgUsername={orgUsername}
                                instanceID={appID}
                                baselineID={baselineID}
                                filterAndSort={filterAndSort.value}
                            />
                        </AppAuthCheck>
                    )
                }
            }
        ],
        [
            abstractRoutePaths.appAddData,
            {
                component: (
                    props: AppDetailRouteComponentProps<{
                        mode: string
                    }>
                ) => {
                    const { appID, mode, orgUsername } = props.match.params
                    const parsedMode: InputMode | undefined =
                        mode === "input" ||
                        mode === "json" ||
                        mode === "csv" ||
                        mode === "sdk"
                            ? mode
                            : undefined

                    if (parsedMode === undefined) {
                        return (
                            <Redirect
                                to={routePaths.appAddData(
                                    orgUsername,
                                    appID,
                                    "input"
                                )}
                            />
                        )
                    }

                    return (
                        <AppAuthCheck
                            {...props.match.params}
                            scope="job:submit"
                        >
                            <AddData
                                key={parsedMode}
                                {...props.match.params}
                                mode={parsedMode}
                            />
                        </AppAuthCheck>
                    )
                }
            }
        ],
        [
            abstractRoutePaths.jobEdit,
            {
                component: (
                    props: AppDetailRouteComponentProps<{
                        jobID: string
                    }>
                ) => {
                    const { appID, jobID, orgUsername } = props.match.params
                    const search = new URLSearchParams(props.location.search)
                    const filterAndSort = decodeFilterAndSort(
                        search,
                        defaultJobSortExpression
                    )

                    if (filterAndSort.isError) {
                        return (
                            <Redirect
                                to={routePaths.jobEdit(
                                    orgUsername,
                                    appID,
                                    jobID
                                )}
                            />
                        )
                    }

                    return (
                        <AppAuthCheck {...props.match.params} scope="job:edit">
                            <JobEdit
                                orgUsername={orgUsername}
                                instanceID={appID}
                                jobID={jobID}
                                params={{
                                    ...filterAndSort.value,
                                    clearFilterOnCloseModal:
                                        search.get(
                                            "clearFilterOnCloseModal"
                                        ) === "true"
                                }}
                                routedFromMessages={
                                    (props.location.state as any)
                                        ?.isMessagesPath
                                }
                            />
                        </AppAuthCheck>
                    )
                },
                exact: true
            }
        ],
        [
            abstractRoutePaths.datasets,
            {
                component: (props: OrgRouteComponentProps) => {
                    return (
                        <AuthCheck>
                            <DataSets
                                orgUsername={props.match.params.orgUsername}
                            />
                        </AuthCheck>
                    )
                }
            }
        ],
        [
            "/dashboard/apis",
            {
                component: ({ location }: RouteComponentProps) => {
                    return (
                        <Redirect
                            to={`${location.pathname.replace(
                                /^\/dashboard\/apis/,
                                "/dashboard/projects"
                            )}${location.search}`}
                        />
                    )
                }
            }
        ],
        [
            "/dashboard/api",
            {
                component: ({ location }: RouteComponentProps) => {
                    return (
                        <Redirect
                            to={`${location.pathname.replace(
                                /^\/dashboard\/api/,
                                "/dashboard/projects"
                            )}${location.search}`}
                        />
                    )
                }
            }
        ],
        [
            abstractRoutePaths.appQualifierTestContentBaselineDataDetail,
            {
                component: ({
                    match,
                    location
                }: AppDetailRouteComponentProps<{
                    qualifierID: string
                    type: turbine.TrainingType
                    baselineID: string
                }>) => {
                    const { appID, qualifierID, baselineID, orgUsername } =
                        match.params

                    const filterAndSort = decodeFilterAndSort(
                        new URLSearchParams(location.search),
                        defaultGroundTruthSort
                    )

                    if (filterAndSort.isError) {
                        return (
                            <Redirect
                                to={routePaths.appQualifierTestContentBaselineDataDetail(
                                    orgUsername,
                                    appID,
                                    qualifierID,
                                    baselineID
                                )}
                            />
                        )
                    }

                    return (
                        <AuthCheck>
                            <AppCustomizeBaselineDataEdit
                                orgUsername={orgUsername}
                                instanceID={appID}
                                baselineID={baselineID}
                                filterAndSort={filterAndSort.value}
                            />
                        </AuthCheck>
                    )
                }
            }
        ],
        [
            abstractRoutePaths.appQualifierTestContent,
            {
                component: (
                    props: AppDetailRouteComponentProps<{
                        qualifierID: string
                    }>
                ) => {
                    return (
                        <AppAuthCheck
                            {...props.match.params}
                            scope="training:create"
                        >
                            <QualifierTestContent {...props} />
                        </AppAuthCheck>
                    )
                }
            }
        ],
        [
            abstractRoutePaths.appQualifierTutorialContent,
            {
                component: (
                    props: AppDetailRouteComponentProps<{
                        qualifierID: string
                    }>
                ) => {
                    return (
                        <AppAuthCheck
                            {...props.match.params}
                            scope="training:create"
                        >
                            <QualifierTutorialContent {...props} />
                        </AppAuthCheck>
                    )
                }
            }
        ],
        [
            abstractRoutePaths.appQualifiers,
            {
                component: (props: AppDetailRouteComponentProps) => {
                    const { appID, orgUsername } = props.match.params

                    return (
                        <AppAuthCheck
                            {...props.match.params}
                            scope="training:create"
                        >
                            <Qualifiers
                                orgUsername={orgUsername}
                                appID={appID}
                            />
                        </AppAuthCheck>
                    )
                }
            }
        ],
        [
            abstractRoutePaths.customerDetails,
            {
                component: (
                    props: RouteComponentProps<{
                        username: string
                    }>
                ) => {
                    const { username } = props.match.params

                    return (
                        <AuthCheck>
                            <BackofficeAuthCheck>
                                <CustomerDetails username={username} />
                            </BackofficeAuthCheck>
                        </AuthCheck>
                    )
                }
            }
        ],
        [
            abstractRoutePaths.customers,
            {
                component: (props: RouteComponentProps) => {
                    const filterAndSort = decodeBackofficeFilterAndSort(
                        new URLSearchParams(props.location.search)
                    )

                    if (filterAndSort.isError) {
                        return <Redirect to={abstractRoutePaths.customers} />
                    }
                    const params =
                        decodeBackofficeFilterAndSortToBackofficeParams(
                            filterAndSort.value
                        )
                    if (params.isError) {
                        return <Redirect to={abstractRoutePaths.customers} />
                    }
                    return (
                        <AuthCheck>
                            <BackofficeAuthCheck>
                                <CustomerList
                                    filterAndSort={filterAndSort.value}
                                    params={params.value}
                                />
                            </BackofficeAuthCheck>
                        </AuthCheck>
                    )
                }
            }
        ]
    ]
    const storeRoutes: [string, RouteInfo][] = [
        /**
         * Store routes
         */
        [
            abstractRoutePaths.onboardingCreateJob,
            {
                component: ({
                    match
                }: RouteComponentProps<{
                    templateSlug: TemplateSlug
                    mode: string
                }>) => {
                    const mode = match.params.mode

                    return (
                        <Onboarding
                            slug={match.params.templateSlug}
                            stage={TemplateInstantiateStage.createJob}
                            mode={
                                mode === "input" ||
                                mode === "json" ||
                                mode === "csv"
                                    ? mode
                                    : "input"
                            }
                        />
                    )
                }
            }
        ],
        [
            abstractRoutePaths.onboardingSetParameters,
            {
                component: (props: any) => {
                    return (
                        <Onboarding
                            slug={props.match.params.templateSlug}
                            stage={TemplateInstantiateStage.setParameters}
                        />
                    )
                }
            }
        ],
        [
            abstractRoutePaths.onboardingSetUser,
            {
                component: (props: any) => {
                    return (
                        <Onboarding
                            slug={props.match.params.templateSlug}
                            stage={TemplateInstantiateStage.setUser}
                        />
                    )
                }
            }
        ],
        [
            abstractRoutePaths.onboardingLoginUser,
            {
                component: (props: any) => {
                    return (
                        <Onboarding
                            slug={props.match.params.templateSlug}
                            stage={TemplateInstantiateStage.loginUser}
                        />
                    )
                }
            }
        ],
        [
            abstractRoutePaths.onboardingSetLabeler,
            {
                component: (props: any) => {
                    return (
                        <Onboarding
                            slug={props.match.params.templateSlug}
                            stage={TemplateInstantiateStage.selectLabeler}
                        />
                    )
                }
            }
        ],
        [
            abstractRoutePaths.onboardingPrepareLabeling,
            {
                component: (props: any) => {
                    return (
                        <Onboarding
                            slug={props.match.params.templateSlug}
                            stage={TemplateInstantiateStage.prepareLabeling}
                        />
                    )
                }
            }
        ],
        [
            abstractRoutePaths.onboardingSelectProject,
            {
                component: (props: any) => {
                    return (
                        <Onboarding
                            slug={props.match.params.templateSlug}
                            stage={TemplateInstantiateStage.selectProject}
                        />
                    )
                }
            }
        ],
        [
            abstractRoutePaths.onboardingChooseOrganization,
            {
                component: (props: any) => (
                    <AuthCheck>
                        <OrgChooser
                            templateSlug={props.match.params.templateSlug}
                        />
                    </AuthCheck>
                )
            }
        ]
    ]

    const routeList = dashboardRoutes.concat(storeRoutes)

    if (
        process.env.REACT_APP_BUILD_ENV === "local" ||
        process.env.REACT_APP_BUILD_ENV === "dev"
    ) {
        routeList.push([
            abstractRoutePaths.test,
            {
                component: require("../Components/Views/Testing/TestView")
                    .default
            }
        ])
    }

    return routeList
})().map(([path, { component, exact }]) => (
    <Route key={path} path={path} render={component} exact={exact} />
))

export const RoutesIsolated = ({
    initialise,
    isWorking,
    router,
    onboardingTemplate,
    shouldResetScrollY
}: Props) => {
    useEffect(() => {
        if (shouldResetScrollY) {
            window.requestAnimationFrame(() => window.scrollTo(0, 0))
        }
    }, [shouldResetScrollY, router.location.pathname])

    useEffect(() => {
        initialise()
    }, [initialise])

    useFreemiumExpiryCountdown()

    if (isWorking) return <Loader absolute={true} />

    const suspendedOnboarding =
        onboardingTemplate === undefined
            ? undefined
            : routePaths.onboardingChooseOrganization(
                  nameToSlug(onboardingTemplate)
              )

    if (
        suspendedOnboarding !== undefined &&
        router.location.pathname !== suspendedOnboarding
    ) {
        return <Redirect to={suspendedOnboarding} />
    }

    return (
        <Switch>
            {routeMap}
            <Route
                path={abstractRoutePaths.dashboard}
                component={DashboardHomeRoute}
            />
            <Route
                path={abstractRoutePaths.orgMemberList}
                component={MemberListRoute}
                exact={true}
            />
            <Route
                path={abstractRoutePaths.orgMemberSignup}
                component={MemberSignUpRoute}
            />
            <Route
                path={abstractRoutePaths.orgWorkerList}
                component={WorkerListRoute}
                exact={true}
            />
            <Route
                path={abstractRoutePaths.orgWorkerAdd}
                component={WorkerAddRoute}
            />
            <Route
                path={abstractRoutePaths.orgWorkerSignup}
                component={WorkerSignUpRoute}
            />
            <Route
                path={abstractRoutePaths.orgWorkerGroupList}
                component={WorkerGroupListRoute}
                exact={true}
            />
            <Route
                path={abstractRoutePaths.orgWorkerGroupAdd}
                component={WorkerGroupAddRoute}
            />
            <Route
                path={abstractRoutePaths.appDetailLabeling}
                component={TaskLabelingRoute}
            />
            <Route
                path={abstractRoutePaths.jobDetailLabeling}
                component={TaskLabelingRoute}
            />
            <Route
                exact={true}
                path={abstractRoutePaths.appCollaborators}
                component={AppCollaboratorsRoute}
            />
            <Route
                path={abstractRoutePaths.appCollaboratorsSignUp}
                component={AppCollaboratorsSignUpRoute}
            />
            <Route
                path={abstractRoutePaths.appSuperAILabelers}
                component={AppSuperAILabelersRoute}
            />
            <Route
                exact={true}
                path={abstractRoutePaths.appCustomizeBaselineDataDetail}
                component={AppGroundTruthDataRoute}
            />
            <Route
                exact={true}
                path={abstractRoutePaths.appCustomizeBaselineData}
                component={AppGroundTruthDataRoute}
            />
            <Route
                path={abstractRoutePaths.taskDetails}
                component={AppDetailRoute}
            />
            <Route
                path={abstractRoutePaths.taskDetailsMessages}
                component={AppDetailRoute}
            />
            <Route
                path={abstractRoutePaths.jobsDetail}
                component={JobDetailRoute}
                // component={AppDetailRoute}
            />
            <Route
                path={abstractRoutePaths.jobsReview}
                component={AppDetailRoute}
            />
            <Route
                path={abstractRoutePaths.appDetail}
                component={AppDetailRoute}
            />
            <Route
                path={abstractRoutePaths.jobsMessageDetail}
                component={AppDetailRoute}
            />
            <Route
                path={abstractRoutePaths.jobsMessageReview}
                component={AppDetailRoute}
            />
            <Route
                path={abstractRoutePaths.appDetailMessages}
                component={AppDetailRoute}
            />
            <Route
                path={abstractRoutePaths.appModelList}
                component={AppModelListRoute}
                exact={true}
            />
            <Route
                path={abstractRoutePaths.appModelDetail}
                component={AppModelDetailRoute}
            />
            <Route
                path={abstractRoutePaths.appWorkerList}
                component={AppWorkerListRoute}
            />
            <Route
                path={abstractRoutePaths.appWorkerGroupList}
                component={AppWorkerGroupListRoute}
            />
            <Route
                path={abstractRoutePaths.appTrainingList}
                component={AppTrainingListRoute}
                exact={true}
            />
            <Route
                path={abstractRoutePaths.appModelsComparison}
                component={AppModelsComparisonRoute}
                exact={true}
            />
            <Route
                path={abstractRoutePaths.appTrainingInstanceDetail}
                component={AppTrainingInstanceDetailRoute}
            />
            <Route
                path={abstractRoutePaths.authSignIn}
                component={SigninRoute}
            />
            <Route
                path={abstractRoutePaths.authSignUp}
                component={SignupRoute}
            />
            <Route
                path={abstractRoutePaths.authForgotPassword}
                component={ForgotPasswordRoute}
            />
            <Route
                path={abstractRoutePaths.authResetPassword}
                component={ResetPasswordRoute}
            />
            <Route
                path={abstractRoutePaths.authVerify}
                component={VerifyOTPRoute}
            />
            <Route
                path={abstractRoutePaths.schemaPlayground}
                component={PlaygroundRoute}
            />
            <Route
                path={abstractRoutePaths.appCustomizeJobParameters}
                component={AppCustomizeJobParametersRoute}
            />
            <Route
                path={abstractRoutePaths.appCustomizeLLMParameters}
                component={AppCustomizeLLMParametersRoute}
            />
            <Route
                path={abstractRoutePaths.appCustomizeTaskParameters}
                component={AppCustomizeTaskParametersRoute}
            />
            <Redirect to={abstractRoutePaths.dashboard} />
        </Switch>
    )
}

const mapStateToProps = (state: State): StateProps => {
    const { onboardingData } = state.client.browser

    return {
        isWorking:
            !isOpenRoute(state.router.location.pathname) &&
            isAuthTransitioning(state),
        onboardingTemplate:
            onboardingData !== null && onboardingData !== undefined
                ? onboardingData.templateIdentifier
                : undefined,
        // necessary for updating component per route
        router: state.router,
        shouldResetScrollY:
            !state.client.routeInfo.isInModalRoute &&
            !state.client.routeInfo.wasInModalRoute
    }
}

const mapDispatchToProps = (dispatch: MainThunkDispatch): DispatchProps => {
    return {
        initialise: () => dispatch(initAuth())
    }
}

export const Routes = connect(
    mapStateToProps,
    mapDispatchToProps
)(RoutesIsolated)
